/**
 * Where an open grid's hairlines cross: every inner column edge meets every
 * inner row edge. Measured from the cells, since the column count changes
 * with the width, and again whenever the grid resizes.
 */
export function useGridJoins(grid: Ref<HTMLElement | undefined>) {
  const joins = ref<{ x: number; y: number }[]>([])

  function measure() {
    const el = grid.value
    if (!el) return
    const box = el.getBoundingClientRect()
    const xs = new Set<number>()
    const ys = new Set<number>()
    for (const cell of Array.from(el.children) as HTMLElement[]) {
      const r = cell.getBoundingClientRect()
      xs.add(Math.round(r.right - box.left))
      ys.add(Math.round(r.bottom - box.top))
    }
    // The outermost right and bottom edges are the grid's own, not joins.
    const inner = (set: Set<number>) => [...set].sort((a, b) => a - b).slice(0, -1)
    joins.value = inner(xs).flatMap((x) => inner(ys).map((y) => ({ x: x - 0.5, y: y - 0.5 })))
  }

  let observer: ResizeObserver | undefined
  onMounted(() => {
    measure()
    if (grid.value) {
      observer = new ResizeObserver(measure)
      observer.observe(grid.value)
    }
  })
  onUnmounted(() => observer?.disconnect())

  return joins
}
